/**
 * Individual tutorial viewer (supports nested slugs via splat)
 * Example: /u/alpha/tutorials/library/intermediate/data-transformation
 *
 * Features:
 * - Step-by-step viewing (one step at a time)
 * - React Flow visualization for workflow steps
 * - Progress tracking only on Next click
 * - Summary view at the end showing all steps
 */

import { json, type LoaderFunction } from "@remix-run/node";
import { useLoaderData, useNavigate, useParams } from "@remix-run/react";
import { ArrowLeft, List } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { DocTutorialShell } from "#app/components/docs/DocTutorialShell";
import { CelebrationModal } from "#app/components/tutorials/CelebrationModal";
import { ProgressMetrics } from "#app/components/tutorials/ProgressMetrics";
import { useTutorialProgress } from "#app/components/tutorials/ProgressTracker";
import { StepNavigator } from "#app/components/tutorials/StepNavigator";
import { TutorialContent } from "#app/components/tutorials/TutorialContent";
import { TutorialSidebarNav } from "#app/components/tutorials/TutorialSidebarNav";
import {
  TutorialStepViewer,
  getTutorialStepContents,
} from "#app/components/tutorials/TutorialStepViewer";
import { TutorialToc } from "#app/components/tutorials/TutorialToc";
import { TutorialWorkflowViewer } from "#app/components/tutorials/TutorialWorkflowViewer";
import { Button } from "#app/components/ui/button";
import { getRequestLocale } from "#app/i18n/get-request-locale.server";
import { loadTutorial, loadTutorialIndex } from "#app/lib/tutorials/loader";
import { extractWorkflowFromContent } from "#app/lib/tutorials/parser";
import { type PlannerWorkflow } from "#app/utils/types";

export const loader: LoaderFunction = async ({ params, request }) => {
  const locale = await getRequestLocale(request);
  const splat = params["*"];
  const slug = splat?.replace(/\/+$/g, "");
  if (!slug) throw new Response("Tutorial not found", { status: 404 });

  const [tutorial, tutorialIndex] = await Promise.all([
    loadTutorial(slug, locale),
    loadTutorialIndex(locale),
  ]);
  if (!tutorial) throw new Response("Tutorial not found", { status: 404 });

  return json({ tutorial, tutorialIndex, locale });
};

export default function TutorialViewerSplat() {
  const { t } = useTranslation("docs");
  const { tutorial, tutorialIndex } = useLoaderData<{
    tutorial: any;
    tutorialIndex: any[];
  }>();
  const navigate = useNavigate();
  const params = useParams();
  const slug = params["*"] || "";

  const stepContents = useMemo(
    () => getTutorialStepContents(tutorial.content),
    [tutorial.content],
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [showCelebrationModal, setShowCelebrationModal] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  const steps = useMemo(
    () =>
      stepContents.map((s) => ({
        id: s.id,
        title: s.title,
        level: s.level,
      })),
    [stepContents],
  );
  const currentStepContent = stepContents[currentStep]?.content ?? "";
  const workflow = useMemo(
    () => extractWorkflowFromContent(currentStepContent),
    [currentStepContent],
  ) as PlannerWorkflow | null;

  const {
    progress,
    updateProgress,
    markStepCompleted,
    markCheckpointCompleted,
    markStarted,
    markCompleted,
  } = useTutorialProgress(slug);

  // Mark tutorial as started when component mounts
  useEffect(() => {
    if (progress && !progress.started) {
      markStarted();
    }
  }, [progress, markStarted]);

  // Initialize current step from progress
  useEffect(() => {
    if (progress?.currentStep !== undefined && !showSummary) {
      setCurrentStep(progress.currentStep);
    }
  }, [progress?.currentStep, showSummary]);

  // Auto-mark first and last steps as completed when user lands on them
  useEffect(() => {
    if (!progress || steps.length === 0 || showSummary) return;
    const first = steps[0];
    const last = steps.at(-1);
    if (
      currentStep === 0 &&
      first &&
      !progress.completedSteps.includes(first.id)
    ) {
      markStepCompleted(first.id);
    }
    if (
      currentStep === steps.length - 1 &&
      last &&
      !progress.completedSteps.includes(last.id)
    ) {
      markStepCompleted(last.id);
    }
  }, [progress, steps, currentStep, markStepCompleted, showSummary]);

  // Check if all steps are completed (excluding summary)
  useEffect(() => {
    if (progress && steps.length > 0 && !progress.completed) {
      const allStepsCompleted = steps.every((step) =>
        progress.completedSteps.includes(step.id),
      );

      if (allStepsCompleted) {
        markCompleted();
        // Show summary view after completion
        setShowSummary(true);
        // Show celebration modal after a brief delay
        setTimeout(() => {
          setShowCelebrationModal(true);
        }, 500);
      }
    }
  }, [progress, steps, markCompleted]);

  // Handle step change - only track progress when clicking Next
  const handleStepChange = (step: number) => {
    // Don't allow going to summary step directly via navigation
    if (step >= steps.length) {
      if (progress?.completed) {
        setShowSummary(true);
        setCurrentStep(steps.length); // Set to summary step index
      }
      return;
    }

    setCurrentStep(step);
    updateProgress({ currentStep: step });

    // Mark step as completed when user navigates to it via Next button
    if (steps[step]) {
      markStepCompleted(steps[step]!.id);
    }
  };

  // Handle Next button click - this is the only way to track progress
  const handleNext = () => {
    if (showSummary) return; // Already at summary

    // Mark current step as completed when clicking Next
    if (steps[currentStep]) {
      markStepCompleted(steps[currentStep]!.id);
    }

    if (currentStep < steps.length - 1) {
      // Move to next step
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      updateProgress({ currentStep: nextStep });
    } else {
      // On last step, mark it as complete and show summary
      if (steps[currentStep]) {
        markStepCompleted(steps[currentStep]!.id);
      }
      markCompleted();
      setShowSummary(true);
      setCurrentStep(steps.length);
    }
  };

  // Handle Previous button click
  const handlePrevious = () => {
    if (showSummary) {
      setShowSummary(false);
      setCurrentStep(steps.length - 1);
      return;
    }

    if (currentStep > 0) {
      handleStepChange(currentStep - 1);
    }
  };

  const handleCheckpointComplete = (
    checkpointId: string,
    _correct: boolean,
  ) => {
    markCheckpointCompleted(checkpointId);
  };

  // Highlight entry workflow node (tutorial progress ids don't map to workflow node ids)
  const currentWorkflowStepId = useMemo(() => {
    if (!workflow) return undefined;
    return workflow.start_at?.[0] || workflow.steps?.[0]?.id;
  }, [workflow]);

  // Calculate actual progress (excluding summary step)
  const actualSteps = steps.length;
  const isAllStepsCompleted = progress?.completed || false;
  const tocCurrentStep = Math.min(currentStep, Math.max(steps.length - 1, 0));

  return (
    <DocTutorialShell
      activeTab="tutorials"
      leftNav={
        <TutorialSidebarNav
          tutorials={tutorialIndex as any}
          currentSlug={tutorial.slug}
        />
      }
      rightRail={
        steps.length > 0 ? (
          <TutorialToc
            steps={steps}
            currentStep={tocCurrentStep}
            completedSteps={progress?.completedSteps || []}
            onStepChange={(idx) => {
              if (idx < steps.length) {
                setCurrentStep(idx);
                updateProgress({ currentStep: idx });
                setShowSummary(false);
              }
            }}
          />
        ) : null
      }
    >
      <div className="h-full flex flex-col">
        <div className="px-4 py-3 border-b border-[#DDDDDD] bg-white dark:bg-zinc-900">
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate("/u/alpha/tutorials")}
              size="icon-sm"
              className="-ml-2 text-[#535EA4] hover:text-[#141A42] dark:hover:text-zinc-100 hover:bg-[#F0F4FF]"
              aria-label={t("backToTutorials")}
              title={t("backToTutorials")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>

            {steps.length > 0 && (
              <Button
                variant="gradient-outline"
                size="sm"
                className="lg:hidden"
                onClick={() => setMobileTocOpen((v) => !v)}
              >
                <List className="h-4 w-4 mr-2" />
                {t("onThisPage")}
              </Button>
            )}
          </div>

          <div className="mt-3">
            <h1 className="text-2xl font-bold text-[#141A42] dark:text-zinc-100 mb-1.5">
              {tutorial.metadata.title}
            </h1>
            {tutorial.metadata.description && (
              <p className="text-sm text-gray-600 mb-2 dark:text-gray-400">
                {tutorial.metadata.description}
              </p>
            )}
            {actualSteps > 0 && (
              <ProgressMetrics
                progress={progress}
                totalSteps={actualSteps}
                className="mb-2"
              />
            )}
          </div>
        </div>

        <div className="flex-1 p-4 lg:p-6">
          <div className="mx-auto max-w-5xl">
            {steps.length > 0 && mobileTocOpen && (
              <div className="mb-4 lg:hidden">
                <TutorialToc
                  steps={steps}
                  currentStep={showSummary ? steps.length : currentStep}
                  completedSteps={progress?.completedSteps || []}
                  onStepChange={(idx) => {
                    if (idx < steps.length) {
                      handleStepChange(idx);
                    } else if (isAllStepsCompleted) {
                      setShowSummary(true);
                      setCurrentStep(steps.length);
                    }
                    setMobileTocOpen(false);
                  }}
                />
              </div>
            )}

            {/* Summary View - Show all steps at once */}
            {showSummary && isAllStepsCompleted ? (
              <div className="space-y-6">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-[#141A42] dark:text-zinc-100 mb-4">
                    {t("summary")}
                  </h2>
                  <p className="text-gray-600 mb-6 dark:text-gray-400">
                    {t("summaryDescription")}
                  </p>
                </div>

                {/* Workflow Visualization in Summary */}
                {workflow && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-[#141A42] dark:text-zinc-100 mb-4">
                      {t("workflowOverview")}
                    </h3>
                    <TutorialWorkflowViewer
                      workflow={workflow}
                      completedStepIds={progress?.completedSteps || []}
                    />
                  </div>
                )}

                {/* Full Content View */}
                <div>
                  <h3 className="text-xl font-semibold text-[#141A42] dark:text-zinc-100 mb-4">
                    {t("completeTutorialContent")}
                  </h3>
                  <TutorialContent
                    content={tutorial.content}
                    mocks={tutorial.metadata.mocks || []}
                    onCheckpointComplete={handleCheckpointComplete}
                    completedCheckpoints={progress?.completedCheckpoints || []}
                  />
                </div>
              </div>
            ) : (
              /* Step-by-Step View */
              <div className="min-w-0">
                <div className="max-w-4xl">
                  {/* If workflow exists, show React Flow visualization prominently */}
                  {workflow && currentStep < steps.length && (
                    <div className="mb-6">
                      <div className="mb-3">
                        <h3 className="text-lg font-semibold text-[#141A42] dark:text-zinc-100">
                          {t("workflow")}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">
                          {t("workflowDescription")}
                        </p>
                      </div>
                      <TutorialWorkflowViewer
                        workflow={workflow}
                        completedStepIds={progress?.completedSteps || []}
                        currentStepId={currentWorkflowStepId}
                      />
                    </div>
                  )}

                  {/* Step Content - filter out workflow step descriptions if workflow exists */}
                  {currentStep < steps.length && (
                    <TutorialStepViewer
                      fullContent={tutorial.content}
                      currentStepIndex={currentStep}
                      mocks={tutorial.metadata.mocks || []}
                      onCheckpointComplete={handleCheckpointComplete}
                      completedCheckpoints={
                        progress?.completedCheckpoints || []
                      }
                      filterWorkflowSteps={!!workflow}
                      workflowSteps={workflow?.steps || []}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Step Navigator - Only show if not in summary view */}
        {steps.length > 0 && !showSummary && (
          <StepNavigator
            steps={steps}
            currentStep={currentStep}
            onStepChange={handleStepChange}
            completedSteps={progress?.completedSteps || []}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        )}

        {/* Summary Navigation */}
        {showSummary && (
          <div className="px-4 py-2.5 border-t border-[#DDDDDD] bg-white flex items-center justify-between dark:bg-zinc-900">
            <Button
              variant="gradient-outline"
              size="sm"
              onClick={handlePrevious}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("backToSteps")}
            </Button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t("allStepsCompleted")}
            </span>
          </div>
        )}
      </div>

      {/* Celebration Modal */}
      <CelebrationModal
        open={showCelebrationModal}
        onOpenChange={setShowCelebrationModal}
        tutorialTitle={tutorial.metadata.title}
        progress={progress}
        totalSteps={actualSteps}
      />
    </DocTutorialShell>
  );
}
